import { useCallback, useState } from "react";
import { ChevronLeft, Lock, Shield } from "lucide-react";

interface LandingPageField {
  name: string;
  label: string;
  type: "text" | "email" | "password" | "tel" | "number";
  placeholder?: string;
  semanticType?: "generic" | "email" | "username" | "password" | "phone" | "employeeId" | "fullName" | "department" | "verificationCode" | "referenceNumber";
}

interface LandingPage {
  title: string;
  subtitle?: string;
  fields: LandingPageField[];
  submitLabel: string;
  brandName?: string;
  url: string;
}

interface LandingPageSubmission {
  url: string;
  fields: Record<string, string>;
  fieldSemantics?: Record<string, NonNullable<LandingPageField["semanticType"]>>;
}

interface FakeLandingPageProps {
  landingPage: LandingPage;
  onSubmit: (submission: LandingPageSubmission) => void;
  onBack: () => void;
  backLabel?: string;
}

export type { LandingPage, LandingPageField, LandingPageSubmission };

export function FakeLandingPage({
  landingPage,
  onSubmit,
  onBack,
  backLabel = "Back",
}: FakeLandingPageProps) {
  const [values, setValues] = useState<Record<string, string>>({});

  const handleChange = useCallback((name: string, value: string) => {
    setValues(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    const fieldSemantics = Object.fromEntries(
      landingPage.fields
        .filter((field): field is LandingPageField & { semanticType: NonNullable<LandingPageField["semanticType"]> } => typeof field.semanticType === "string" && field.semanticType.length > 0)
        .map(field => [field.name, field.semanticType])
    );

    onSubmit({
      url: landingPage.url,
      fields: values,
      ...(Object.keys(fieldSemantics).length > 0 ? { fieldSemantics } : {}),
    });
  }, [landingPage.fields, landingPage.url, values, onSubmit]);

  const hasInput = Object.values(values).some(v => v.trim().length > 0);

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-[#F2F2F7] dark:bg-[#1C1C1E]">
      {/* Browser chrome — address bar */}
      <div className="shrink-0 flex items-center gap-2 border-b border-black/10 bg-white/90 px-2 py-1.5 backdrop-blur-sm dark:border-white/10 dark:bg-[#2C2C2E]/90">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-0.5 text-[13px] font-medium text-blue-500 dark:text-blue-400"
        >
          <ChevronLeft size={16} />
          {backLabel}
        </button>
        <div className="flex min-w-0 flex-1 items-center gap-1 rounded-lg bg-black/5 px-2 py-1 dark:bg-white/10">
          <Lock size={10} className="shrink-0 text-[#8E8E93]" />
          <span className="truncate text-[11px] text-[#8E8E93]">
            {landingPage.url}
          </span>
        </div>
      </div>

      {/* Page content */}
      <div className="flex-1 overflow-y-auto px-4 pt-6 pb-4">
        {/* Brand header */}
        <div className="flex flex-col items-center gap-2 pb-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-md">
            <Shield size={22} className="text-white" />
          </div>
          {landingPage.brandName && (
            <span className="text-[13px] font-semibold text-[#3C3C43]/80 dark:text-[#EBEBF5]/70">
              {landingPage.brandName}
            </span>
          )}
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-black/5 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-[#2C2C2E]">
          <h2 className="text-center text-[17px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">
            {landingPage.title}
          </h2>
          {landingPage.subtitle && (
            <p className="mt-1 text-center text-[13px] text-[#3C3C43]/70 dark:text-[#EBEBF5]/60">
              {landingPage.subtitle}
            </p>
          )}

          {/* Form fields */}
          <div className="mt-5 flex flex-col gap-3">
            {landingPage.fields.map(field => (
              <div key={field.name}>
                <label className="mb-1 block text-[12px] font-medium text-[#3C3C43]/80 dark:text-[#EBEBF5]/70">
                  {field.label}
                </label>
                <input
                  type={field.type}
                  placeholder={field.placeholder}
                  value={values[field.name] || ""}
                  onChange={e => handleChange(field.name, e.target.value)}
                  className="w-full rounded-lg border border-black/10 bg-[#F2F2F7] px-3 py-2.5 text-[14px] text-[#1C1C1E] placeholder-[#8E8E93] outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-white/15 dark:bg-[#1C1C1E] dark:text-[#F2F2F7]"
                  autoComplete="off"
                />
              </div>
            ))}
          </div>

          {/* Submit button */}
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!hasInput}
            className="mt-5 w-full rounded-lg bg-blue-500 py-2.5 text-[15px] font-semibold text-white transition-opacity disabled:opacity-40 dark:bg-blue-600"
          >
            {landingPage.submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
